import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLine } from "@/components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "The Origin",
    body: "Majnun started in Fort Worth, Texas. This brand is not about arriving. It is about the walk. The stumbling. The doubt. The getting back up.",
  },
  {
    n: "02",
    title: "The Material",
    body: "Faith, to us, was never clean or comfortable. It is raw. It carries scars. Our garments hold that same tension: heavyweight fabrics, exposed seams, garment-dyed shadows. Built to be worn hard on the long road.",
  },
  {
    n: "03",
    title: "The Reaching",
    body: "Every piece is a step in the journey. We are fallen, but still reaching — moving toward a light we cannot yet see but refuse to stop believing in.",
  },
];

const Story = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);

  return (
    <div data-testid="story-page">
      <section
        ref={heroRef}
        className="relative min-h-[68vh] flex items-center justify-center overflow-hidden border-b border-border"
      >
        <motion.img
          src="https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/kbqq1xsp_IMG_6801.webp"
          alt="Manifesto"
          style={{ y, scale }}
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
        />
        <div className="absolute inset-0 bg-background/70" />
        <h1 className="relative font-serif text-6xl sm:text-8xl uppercase tracking-tighter text-center leading-[0.82] px-4">
          <MaskLine index={0}>The</MaskLine>
          <MaskLine index={1}>Manifesto</MaskLine>
        </h1>
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-8 py-24 space-y-16">
        <Reveal>
          <p className="font-serif text-3xl sm:text-4xl leading-tight">
            MAJNUN — the one consumed by sin, yet driven mad in pursuit of the divine. A soul that
            keeps reaching, even after the fall.
          </p>
        </Reveal>

        <div className="divide-y divide-border border-y border-border">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.06}>
              <div className="grid grid-cols-12 gap-4 py-10 group">
                <div className="col-span-3 sm:col-span-2">
                  <span className="font-serif text-4xl sm:text-5xl text-[#5B2A86] group-hover:text-[#9D4EDD] transition-colors">
                    {c.n}
                  </span>
                </div>
                <div className="col-span-9 sm:col-span-10">
                  <h3 className="font-serif text-2xl sm:text-3xl uppercase tracking-tight mb-3">
                    {c.title}
                  </h3>
                  <p className="font-mono text-sm text-muted-foreground leading-relaxed max-w-xl">
                    {c.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <blockquote className="border-l-2 border-[#5B2A86] pl-6 font-serif italic text-2xl">
            “A walk of faith — the wound and the worship, worn as one. The road to the divine was
            never meant to be easy.”
          </blockquote>
        </Reveal>

        <div className="grid grid-cols-2 gap-5 pt-2">
          <Reveal className="overflow-hidden">
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/zbunyz76_IMG_6782.webp"
              alt=""
              className="w-full aspect-[3/4] object-cover transition-transform duration-700 hover:scale-105"
            />
          </Reveal>
          <Reveal className="overflow-hidden mt-10" delay={0.1}>
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/vot65fw7_IMG_6813.webp"
              alt=""
              className="w-full aspect-[3/4] object-cover transition-transform duration-700 hover:scale-105"
            />
          </Reveal>
        </div>

        <div className="text-center pt-8">
          <Link
            to="/shop"
            data-testid="story-shop-button"
            className="inline-block bg-foreground text-background px-10 py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors"
          >
            Walk with us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Story;
